import { SignalRService } from '@/api/bff';
import { ProcessesService, ProcessStatus } from '@/api/process';
import { StockOnHandAlignmentService, StockOnHandItems } from '@/api/receive';
import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { CTAContainer } from '@/components/layout/CTAContainer';
import { ModalDataSavedError as Modal } from '@/components/layout/ModalDataSaved';
import { AlertSnackbar } from '@/components/ui/AlertSnackbar';
import { UIBox } from '@/components/ui/Box';
import { UIButtonWithIcon } from '@/components/ui/Button';
import { ErrorSnackbar } from '@/components/ui/ErrorSnackbar';
import { PageLoader } from '@/components/ui/PageLoader';
import { Typography } from '@/components/ui/Typography';
import {
  initMQTTTags,
  removeDeviceInUse,
} from '@/features/devices/devicesSlice';
import { useSubscribeDeviceStatus } from '@/hooks/mqtt/useSubscribeDeviceStatus';
import useHandleProcess from '@/hooks/useHandleProcess';
import useResetDevice from '@/hooks/useResetDevice';
import { useSelector } from '@/hooks/useSelector';
import * as S from '@/pages/CycleCount/PageSoh/style';
import RecapItem from '@/pages/CycleCount/PageSohKickoff/RecapItem';
import { TColorName } from '@/theme';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useAsync } from 'react-use';
import styled from 'styled-components';
import SohaKickoffModal from '../../../components/ui/SohaKickoffModal/SohaKickoffModal';

export const StyledErrorOutlineIcon = styled(ErrorOutlineIcon)`
  fill: ${({ theme }): TColorName => theme.palette.colors.yellow};
  vertical-align: middle;
`;

const Index: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const handleProcess = useHandleProcess();
  const { resetDevice } = useResetDevice();
  const deviceInUse = useSelector(state => state.devices.deviceInUse);
  const storeCode = useSelector(
    state => state.currentStore.store?.storeCode || ''
  );
  const user = useSelector(state => state.user);

  const subscribeDevice = useSubscribeDeviceStatus();
  const { deviceList } = useSelector(state => state.devices);

  const [onGoingRecap, setOngoingRecap] = React.useState<StockOnHandItems[]>(
    []
  );

  const [id, setId] = React.useState<string>('');
  const [fetchRecapError, setFetchRecapError] = React.useState<boolean>(false);
  const [sohaUnlockWentWrongModal, setSohaUnlockWentWrongModal] =
    React.useState<boolean>(false);
  const [sohaUnlockSuccess, setSohaUnlockSuccess] =
    React.useState<boolean>(false);
  const [confirmSohaUnlock, setConfirmSohaUnlock] =
    React.useState<boolean>(false);
  const [confirmGoToSoha, setConfirmGoToSoha] = React.useState<boolean>(false);
  const [fetchRecapLoading, setFetchRecapLoading] =
    React.useState<boolean>(false);
  const [soha, setSoha] = React.useState<ProcessStatus | null>(null);

  const fetchSohRecap = React.useCallback(async () => {
    try {
      setFetchRecapLoading(true);

      const response =
        await StockOnHandAlignmentService.stockonhandRecapSohAlignment({
          storeCode,
        });

      if (response && response.stockOnHandItemsList.length > 0) {
        setOngoingRecap(
          response.stockOnHandItemsList.filter(
            ({ stockOnHandStatus }) => stockOnHandStatus === 'ONGOING'
          )
        );
      }

      setFetchRecapLoading(false);
    } catch {
      setFetchRecapLoading(false);
      setFetchRecapError(true);
    }
  }, [storeCode]);

  useAsync(async () => {
    dispatch(initMQTTTags());

    if (deviceInUse) {
      await resetDevice();
    }
  }, []);

  useAsync(async () => {
    await fetchSohRecap();
  }, []);

  useAsync(async () => {
    const sohProcess: ProcessStatus[] =
      (await ProcessesService.readProcessStatus({
        storeCode,
        process: 'SOHA',
        filterByStoreCode: true,
      })) || [];
    setSoha(sohProcess?.[0]);
  }, [storeCode]);

  const onReleaseClick = async (): Promise<void> => {
    try {
      if (soha) {
        // DELETE SU SOHA -> cancellazione dati/servizi su soha con gestione dell'errore
        try {
          await StockOnHandAlignmentService.stockonhandDeleteStockOnHand({
            storeCode,
            stockOnHandId: id,
          });
        } catch (error) {
          console.error(
            'StockOnHandAlignmentService.stockonhandDeleteStockOnHand - ',
            error
          );
        }
        // CANCELLARE CHIAVE SU REDIS
        try {
          await SignalRService.signalRUnSubscribeProcess({
            requestBody: {
              storeCode: storeCode,
              process: 'SOHA',
              userId: soha.lockedBy || '',
            },
          });
        } catch (error) {
          console.error('SignalRService.signalRUnSubscribeProcess - ', error);
        }
        // handling process status settando user corretto (owner del soha bloccante ogoing) nel payload
        if (soha && soha.isLocked) {
          try {
            await handleProcess({
              process: 'SOHA',
              isLocked: false,
              lockedBy: soha.lockedBy,
            });
          } catch (error) {
            console.error('handleProcess - ', error);
          }
        }
        // unlock device forzando la scrittura su MQTT
        deviceList.forEach(({ deviceId }) => {
          try {
            subscribeDevice.publish(deviceId!, undefined);
            dispatch(removeDeviceInUse());
          } catch (error) {
            console.error(`subscribeDevice.publish(${deviceId}!, undefined); - `, error);
          }
        });
        await resetDevice();

        setSohaUnlockSuccess(true);
        setConfirmSohaUnlock(false);
        await fetchSohRecap();
      }
    } catch (error) {
      setConfirmSohaUnlock(false);
      setSohaUnlockWentWrongModal(true);
    }
  };

  const handleClick = (id: string): void => {
    setId(id);
    if (user.username === soha?.lockedBy) {
      setConfirmGoToSoha(true);
    } else {
      setConfirmSohaUnlock(true);
    }
  };

  return (
    <>
      {fetchRecapLoading ? (
        <PageLoader />
      ) : (
        <>
          <Modal
            iconType="ERROR"
            $minWidth="420px"
            $minHeight="230px"
            title={t('error.something_went_wrong')}
            message={t('sohaUnlockWentWrongMessage')}
            open={sohaUnlockWentWrongModal}
            onClose={async (): Promise<void> => {
              setSohaUnlockWentWrongModal(false);
              setConfirmSohaUnlock(false);
              await fetchSohRecap();
            }}
          />
          {/* soha lockedBy != current user */}
          <SohaKickoffModal
            open={confirmSohaUnlock}
            onClose={async (): Promise<void> => setConfirmSohaUnlock(false)}
            title={t('payAttention')}
            message={t('soha.kickoff.message')}
            onConfirm={onReleaseClick}
          />
          {/* soha lockedBy === current user */}
          <SohaKickoffModal
            open={confirmGoToSoha}
            onClose={async (): Promise<void> => setConfirmGoToSoha(false)}
            title={t('payAttention')}
            message={t('soha.kickoff.goToSohaMessage')}
            onConfirm={() =>
              history.push(`/counting-items/soh-alignment/scan?id=${id}`)
            }
            hideCheck={true}
          />
          <AlertSnackbar
            severity="success"
            open={sohaUnlockSuccess}
            setIsOpen={setSohaUnlockSuccess}
            message={t('success.itemDeleted')}
          />

          <>
            <UIBox width="100%" flexDirection="column" p={3}>
              <UIBox display="flex" alignItems="center" py={1}>
                <StyledErrorOutlineIcon fill="orange" />
                <Typography size="lg" font="heavy">
                  {t('attention')}
                </Typography>
              </UIBox>
              <Typography size="lg" font="heavy" style={{ lineHeight: '24px' }}>
                {t('soha.kickoff.message')}
              </Typography>
            </UIBox>
            <UIBox
              width="100%"
              flexDirection="column"
              p={3}
              position={'relative'}
            >
              <UIBox
                mb={2}
                width={'100%'}
                justifyContent={'end'}
                position={'absolute'}
                top={0}
                right={'1.5rem'}
              >
                <UIButtonWithIcon
                  label={t('refresh')}
                  startIcon={<RefreshIcon />}
                  onClick={fetchSohRecap}
                />
              </UIBox>
              {onGoingRecap.length > 0 ? (
                <>
                  <UIBox mb={1}>
                    <Typography size="lg" font="heavy">
                      {t('onGoingSoh')}
                    </Typography>
                  </UIBox>
                  <S.List mb="48px">
                    {onGoingRecap.map(item => (
                      <RecapItem
                        key={item.id}
                        onClick={(id: string) => handleClick(id)}
                        lockingUser={soha?.lockedBy || ''}
                        {...item}
                      />
                    ))}
                  </S.List>
                </>
              ) : (
                <UIBox
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  height={'100%'}
                  width={'100%'}
                  py={12}
                >
                  <Typography>{t('soha.kickoff.noitemsfound')}</Typography>
                </UIBox>
              )}
            </UIBox>
          </>
          <CTAContainer
            type="BACK"
            onClick={(): void => {
              history.push(AppRoutes.INTRO);
            }}
          />
        </>
      )}
      <ErrorSnackbar
        open={fetchRecapError}
        setIsOpen={setFetchRecapError}
        errorMessage={t('error.fetchSohRecap')}
      />
    </>
  );
};

export default Index;
